import React, { useState, useEffect } from 'react';
import { collection, addDoc, getDocs, doc, updateDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '@/firebase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Edit, Trash2, Plus, Save, X } from 'lucide-react';
import { toast, Toaster } from 'sonner';

export const AdminLabTests = () => {
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [newTest, setNewTest] = useState({ testName: '', price: '', category: 'Blood', sample: 'Blood', popular: false });

  const categories = ['Blood', 'Diabetic', 'Thyroid', 'Cardiac', 'Liver', 'Kidney', 'Package'];
  const sampleTypes = ['Blood', 'Urine', 'Stool', 'Sputum', 'Multiple'];

  useEffect(() => {
    fetchTests();
  }, []);

  const fetchTests = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'lab_tests'));
      const testsData = [];
      querySnapshot.forEach((doc) => {
        testsData.push({ id: doc.id, ...doc.data() });
      });
      testsData.sort((a, b) => (a.testName || '').localeCompare(b.testName || ''));
      setTests(testsData);
    } catch (error) {
      console.error('Error fetching tests:', error);
      toast.error('Failed to load tests');
    }
  };

  const handleAddTest = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await addDoc(collection(db, 'lab_tests'), {
        ...newTest,
        price: parseFloat(newTest.price),
        createdAt: serverTimestamp()
      });
      toast.success('Test added successfully!');
      setNewTest({ testName: '', price: '', category: 'Blood', sample: 'Blood', popular: false });
      fetchTests();
    } catch (error) {
      console.error('Error adding test:', error);
      toast.error('Failed to add test');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateTest = async (id, updatedTest) => {
    setLoading(true);
    try {
      await updateDoc(doc(db, 'lab_tests', id), {
        ...updatedTest,
        price: parseFloat(updatedTest.price),
        updatedAt: serverTimestamp()
      });
      toast.success('Test updated successfully!');
      setEditingId(null);
      fetchTests();
    } catch (error) {
      console.error('Error updating test:', error);
      toast.error('Failed to update test');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTest = async (id) => {
    if (!window.confirm('Are you sure you want to delete this test?')) return;
    setLoading(true);
    try {
      await deleteDoc(doc(db, 'lab_tests', id));
      toast.success('Test deleted successfully!');
      fetchTests();
    } catch (error) {
      console.error('Error deleting test:', error);
      toast.error('Failed to delete test');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-8">
      <Toaster position="top-center" richColors />
      
      {/* Add New Test Form */}
      <Card className="p-6 bg-gradient-to-br from-rose-50 to-pink-50">
        <h2 className="text-2xl font-bold text-rose-900 mb-6">Add New Lab Test</h2>
        <form onSubmit={handleAddTest} className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>Test Name *</Label>
              <Input
                value={newTest.testName}
                onChange={(e) => setNewTest({ ...newTest, testName: e.target.value })}
                placeholder="e.g., CBC, Lipid Profile"
                required
              />
            </div>
            <div>
              <Label>Price (₹) *</Label>
              <Input
                type="number"
                step="0.01"
                value={newTest.price}
                onChange={(e) => setNewTest({ ...newTest, price: e.target.value })}
                placeholder="0.00"
                required
              />
            </div>
            <div>
              <Label>Category *</Label>
              <select
                value={newTest.category}
                onChange={(e) => setNewTest({ ...newTest, category: e.target.value })}
                className="flex h-9 w-full rounded-md border border-slate-300 bg-white px-3 py-1 text-base"
              >
                {categories.map((cat) => <option key={cat} value={cat}>{cat}</option>)}
              </select>
            </div>
            <div>
              <Label>Sample Type *</Label>
              <select
                value={newTest.sample}
                onChange={(e) => setNewTest({ ...newTest, sample: e.target.value })}
                className="flex h-9 w-full rounded-md border border-slate-300 bg-white px-3 py-1 text-base"
              >
                {sampleTypes.map((type) => <option key={type} value={type}>{type}</option>)}
              </select>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="popular"
              checked={newTest.popular}
              onChange={(e) => setNewTest({ ...newTest, popular: e.target.checked })}
              className="w-4 h-4"
            />
            <Label htmlFor="popular" className="cursor-pointer">Mark as Popular Test</Label>
          </div>
          <Button type="submit" disabled={loading} className="bg-gradient-to-r from-rose-600 to-pink-600 text-white">
            <Plus className="w-4 h-4 mr-2" />
            Add Test
          </Button>
        </form>
      </Card>

      {/* Tests List */}
      <Card className="p-6">
        <h2 className="text-2xl font-bold text-slate-900 mb-6">Manage Lab Tests ({tests.length})</h2>
        <div className="space-y-3 max-h-[600px] overflow-y-auto">
          {tests.map((test) => (
            <TestRow
              key={test.id}
              test={test}
              isEditing={editingId === test.id}
              onEdit={() => setEditingId(test.id)}
              onCancelEdit={() => setEditingId(null)}
              onSave={handleUpdateTest}
              onDelete={handleDeleteTest}
              loading={loading}
              categories={categories}
              sampleTypes={sampleTypes}
            />
          ))}
          {tests.length === 0 && (
            <p className="text-center text-slate-500 py-8">No lab tests found. Add your first test above.</p>
          )}
        </div>
      </Card>
    </div>
  );
};

const TestRow = ({ test, isEditing, onEdit, onCancelEdit, onSave, onDelete, loading, categories, sampleTypes }) => {
  const [editData, setEditData] = useState({ ...test });

  if (isEditing) {
    return (
      <div className="bg-rose-50 border-2 border-rose-300 rounded-lg p-4">
        <div className="grid md:grid-cols-4 gap-3">
          <Input
            value={editData.testName}
            onChange={(e) => setEditData({ ...editData, testName: e.target.value })}
            placeholder="Test Name"
          />
          <Input
            type="number"
            step="0.01"
            value={editData.price}
            onChange={(e) => setEditData({ ...editData, price: e.target.value })}
            placeholder="Price"
          />
          <select
            value={editData.category}
            onChange={(e) => setEditData({ ...editData, category: e.target.value })}
            className="flex h-9 rounded-md border border-slate-300 bg-white px-3 py-1 text-sm"
          >
            {categories.map((cat) => <option key={cat} value={cat}>{cat}</option>)}
          </select>
          <select
            value={editData.sample}
            onChange={(e) => setEditData({ ...editData, sample: e.target.value })}
            className="flex h-9 rounded-md border border-slate-300 bg-white px-3 py-1 text-sm"
          >
            {sampleTypes.map((type) => <option key={type} value={type}>{type}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-2 mt-3">
          <input
            type="checkbox"
            checked={editData.popular || false}
            onChange={(e) => setEditData({ ...editData, popular: e.target.checked })}
            className="w-4 h-4"
          />
          <span className="text-sm">Popular</span>
        </div>
        <div className="flex gap-2 mt-4">
          <Button size="sm" onClick={() => onSave(test.id, editData)} disabled={loading}>
            <Save className="w-4 h-4 mr-1" /> Save
          </Button>
          <Button size="sm" variant="outline" onClick={onCancelEdit}>
            <X className="w-4 h-4 mr-1" /> Cancel
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between p-4 bg-white border border-slate-200 rounded-lg hover:border-rose-300 transition-colors">
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <h3 className="font-semibold text-slate-900">{test.testName}</h3>
          {test.popular && <span className="text-xs bg-rose-100 text-rose-700 px-2 py-1 rounded">Popular</span>}
        </div>
        <div className="flex gap-4 mt-1 text-sm text-slate-600">
          <span>₹{test.price?.toFixed(2)}</span>
          <span>•</span>
          <span>{test.category}</span>
          <span>•</span>
          <span>{test.sample}</span>
        </div>
      </div>
      <div className="flex gap-2">
        <Button size="sm" variant="outline" onClick={onEdit}>
          <Edit className="w-4 h-4" />
        </Button>
        <Button size="sm" variant="outline" onClick={() => onDelete(test.id)} className="text-red-600 hover:bg-red-50">
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};